import type { Message } from '../hooks/useLessonState';
import { INTERFACE_TEXT } from '../constants/interfaceText';

/** Failed plays of a loaded clip before the learner is offered a skip. */
export const PLAY_FAILURES_BEFORE_SKIP = 2;

export interface AudioRecovery {
  text: string;
  /** Re-fetch the phrase's clips. */
  canRetry: boolean;
  /** Skip a phrase that cannot be played (#249). Never offered for a phrase that has a working clip. */
  canSkip: boolean;
}

/**
 * What a phrase with a broken clip offers: the one place that decides when Skip appears, shared by the
 * chat message and the turn card so they cannot disagree (BR-008: a phrase the learner hasn't heard is
 * never skipped past while its clip works). A check-in line (audio only) offers a retry and never a skip
 * or a text fallback (BR-043).
 */
export function audioRecovery(
  message: Message,
  retryFailed: boolean,
  playFailures: number
): AudioRecovery | null {
  const fetchFailed = message.audioUnavailable === 'fetch_failed';
  if (message.audioOnly && message.audioUnavailable) {
    return {
      text: fetchFailed
        ? INTERFACE_TEXT.tcAudioOnlyFetchFailed
        : INTERFACE_TEXT.tcAudioOnlyMissing,
      canRetry: fetchFailed,
      canSkip: false,
    };
  }
  if (fetchFailed) {
    return {
      text: INTERFACE_TEXT.tcAudioFetchFailed,
      canRetry: true,
      canSkip: retryFailed,
    };
  }
  if (message.audioUnavailable === 'missing') {
    return {
      text: INTERFACE_TEXT.tcAudioMissing,
      canRetry: false,
      canSkip: true,
    };
  }
  if (
    !!message.audioUrls?.natural &&
    playFailures >= PLAY_FAILURES_BEFORE_SKIP
  ) {
    return {
      text: INTERFACE_TEXT.tcAudioWontPlay,
      canRetry: false,
      canSkip: true,
    };
  }
  return null;
}
